import { useCallback, useEffect, useRef, useState } from 'react';
import api, { errorText } from '../services/api';

/** GET a path; refetches when the path changes. `reload()` refetches without blanking the current data. */
export function useApi<T>(path: string | null, intervalMs?: number) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(!!path);
  const current = useRef(path);
  current.current = path;

  const load = useCallback(async (quiet = false) => {
    if (!path) return;
    if (!quiet) setLoading(true);
    try {
      const res = await api.get<T>(path);
      if (current.current === path) { setData(res.data); setError(null); }
    } catch (e) {
      if (current.current === path) setError(errorText(e));
    } finally {
      if (current.current === path) setLoading(false);
    }
  }, [path]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!intervalMs) return;
    const id = window.setInterval(() => { if (!document.hidden) load(true); }, intervalMs);
    return () => window.clearInterval(id);
  }, [load, intervalMs]);

  return { data, error, loading: loading && !data, refreshing: loading && !!data, reload: () => load(true) };
}
